'use client';

import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { utilsStyles } from '../../../..';
import iconCoinSmall from '../../../../assets/images/icons/coin-small.png';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FormSelect from '../../../../components/form/FormSelect/FormSelect';
import Accordion from '../../../../components/ui/Accordion/Accordion';
import IconTextUnit from '../../../../components/ui/IconTextUnit/IconTextUnit';
import type { CalcCoinResult, MultiDrawItem } from '../../types';
import styles from './LotteryCoinForm.module.scss';
export type LotteryCoinFormProps<T = unknown> = {
  lotteryEventId: number;
  drawFee: number;
  coinMenuItem: T;
  calcResult: CalcCoinResult;
  multiDraw: Array<MultiDrawItem>;
  storageKey: string;
  showPaymentWarning?: boolean;
  userCoin?: number;
  sessionStatus: 'loading' | 'authenticated' | 'unauthenticated';
  // 処理系の関数
  onCalcCoin: (params: { lotteryEventId: number; drawCount: number }) => Promise<{ result: CalcCoinResult }>;
  onPurchase: (coinMenuItem: T, missingCoin: number) => Promise<void>;
  onInvalid: () => void;
  onError: (messages: string[]) => Promise<void>;
  formatCurrency: (amount: number) => string;
};

function LotteryCoinForm<T>({
  lotteryEventId,
  drawFee,
  coinMenuItem,
  calcResult,
  multiDraw,
  storageKey,
  showPaymentWarning = false,
  userCoin = 0,
  sessionStatus,
  onCalcCoin,
  onPurchase,
  onInvalid,
  onError,
  formatCurrency,
}: LotteryCoinFormProps<T>) {
  const initCalcCoinResult: CalcCoinResult = calcResult;

  const [calcCoinResult, setCalcCoinResult] = useState(initCalcCoinResult);
  const [missingCoin, setMissingCoin] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  const { handleSubmit, getValues, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      count: 1,
    },
  });

  const countOptions = [...Array(100).keys()].map((v) => {
    return {
      value: v + 1,
      text: (v + 1).toString(),
    };
  });

  //購入処理
  const purchase = async () => {
    try {
      setIsLoading(true);
      await setMaxDrawableMenuId(lotteryEventId);
      await onPurchase(coinMenuItem, missingCoin);
      setIsLoading(false);
    } catch (_error) {
      setIsLoading(false);
    }
  };

  const submitError = () => {
    onInvalid();
  };

  //コイン計算
  const calcCoin = useCallback(async () => {
    try {
      const { result } = await onCalcCoin({
        lotteryEventId: lotteryEventId,
        drawCount: getValues('count'),
      });

      setCalcCoinResult(result);

      //不足分を計算
      const totalDrawCoin = result.drawCoin + result.shippingCoin;
      const calcMissingCoin = totalDrawCoin - userCoin;
      setMissingCoin(calcMissingCoin < 0 ? 0 : calcMissingCoin);
    } catch (_error) {
      await onError(['エラーが発生しました']);
    }
  }, [getValues, lotteryEventId, userCoin, onCalcCoin, onError]);

  // 初回表示時にコイン不足時に不足分のコインを購入するボタンが動作するようにする
  useEffect(() => {
    // calcCoinが複数回実行されないようにセッションが認証されてuserCoinが更新された後に実行
    if (sessionStatus === 'authenticated') {
      calcCoin();
    }
  }, [calcCoin, sessionStatus]);

  // 購入後のコインで引ける最大回数のメニューIDをストレージに保存
  const setMaxDrawableMenuId = async (lotteryEventId: number) => {
    try {
      const afterPurchasedTotal = calcCoinResult.drawCoin + calcCoinResult.shippingCoin;
      // 購入コイン数＋現在コイン数で引ける最大のメニューを取得
      for (const item of multiDraw.filter((item) => item.count * drawFee <= afterPurchasedTotal)) {
        const { result } = await onCalcCoin({
          lotteryEventId: lotteryEventId,
          drawCount: item.count,
        });

        if (afterPurchasedTotal < result.drawCoin + result.shippingCoin) {
          continue;
        }
        localStorage.setItem(storageKey, item.id.toString());
        break;
      }
    } catch (_error) {
      await onError(['エラーが発生しました']);
    }
  };

  return (
    <>
      <div className={utilsStyles.mtMedium}>
        <div className={`${utilsStyles.flexRowNowrap} ${utilsStyles.alignCenter} ${utilsStyles.spaceBetween}`}>
          <div>
            <p className={`${utilsStyles.textMedium} ${utilsStyles.textBold}`}>保有しているコイン</p>
            <div className={utilsStyles.textLarge}>
              <IconTextUnit text={formatCurrency(userCoin)} imgSrc={iconCoinSmall.src} />
            </div>
          </div>
          <ActionButton color='primary' size='small' onClick={() => setIsOpened(!isOpened)}>
            回数を指定して購入
          </ActionButton>
        </div>
      </div>

      <Accordion isOpened={isOpened} startHeight={0}>
        <div className={utilsStyles.mtSmall}>
          <div className={`${utilsStyles.centered} ${utilsStyles.spaceBetween}`}>
            <h1 className={utilsStyles.pageTitle}>くじ引き回数分のコインを購入</h1>
          </div>
        </div>
        <div className={utilsStyles.mtSmall}>
          <div className={styles.lotteryCoinForm}>
            <div className={styles.formHeadWrapper}>
              {/* くじ引きコイン数 */}
              <div className={utilsStyles.centered}>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>単価:</div>
                <div className={`${styles.formTextSize} ${utilsStyles.textBold} ${utilsStyles.mLeftXSmall}`}>
                  <IconTextUnit text={formatCurrency(drawFee)} imgSrc='/images/icons/coin-small.png' />
                </div>
              </div>
              {/* 回数選択 */}
              <div className={styles.formSelectWrapper}>
                <div className={`${utilsStyles.textBold} ${styles.formTextSize}`}>くじ引き回数:</div>
                <div className={`${styles.formSelectItem} ${utilsStyles.mLeftXSmall}`}>
                  <FormSelect
                    label=''
                    name='count'
                    items={countOptions}
                    control={control}
                    required
                    outlined
                    onChange={() => calcCoin()}
                  />
                </div>
              </div>
            </div>
            <div className={utilsStyles.mtSmall}>
              <hr className={utilsStyles.pinkishgrayBgColor} />
            </div>
            {/* コイン詳細 */}
            <div className={`${styles.formDetail} ${utilsStyles.mtSmall}`}>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>小計</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit text={formatCurrency(calcCoinResult.drawCoin)} imgSrc='/images/icons/coin-small.png' />
                </div>
              </div>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>配送手数料</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit
                    text={formatCurrency(calcCoinResult.shippingCoin)}
                    imgSrc='/images/icons/coin-small.png'
                  />
                </div>
              </div>
              <div className={styles.formDetailRow}>
                <hr className={utilsStyles.pinkishgrayBgColor} />
              </div>
              <div className={styles.formDetailRow}>
                <p className={styles.formTextSize}>合計</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit
                    text={formatCurrency(calcCoinResult.drawCoin + calcCoinResult.shippingCoin)}
                    imgSrc='/images/icons/coin-small.png'
                  />
                </div>
              </div>
              <div
                className={`${styles.formDetailRow} ${utilsStyles.primaryColor}`}
                style={{ opacity: missingCoin > 0 ? 1 : 0.3 }}
              >
                <p className={styles.formTextSize}>不足分</p>
                <div className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                  <IconTextUnit text={formatCurrency(missingCoin)} imgSrc='/images/icons/coin-primary.png' />
                </div>
              </div>
            </div>
            <div className={styles.buttonWrapper}>
              <ActionButton
                onClick={handleSubmit(purchase, submitError)}
                disabled={missingCoin === 0 || isLoading}
                size='fullWidth'
                color='primary'
              >
                不足分のコインを購入する
              </ActionButton>
            </div>
            {showPaymentWarning && (
              <div
                className={`${utilsStyles.mtSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold} ${utilsStyles.primaryColor}`}
              >
                <p>SNSアプリ内ブラウザやシークレットモードをご利用の場合、決済に失敗する可能性がございます。</p>
                <br />
                <p>決済エラーを防ぐには：</p>
                <ul>
                  <li>
                    ・SNSアプリ内ではなく、SafariやChromeなどのブラウザアプリから直接サイトにアクセスしてください。
                  </li>
                  <li>・ブラウザのシークレットモードやプライベートモードの使用を避けてください。</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </Accordion>
    </>
  );
}

export default LotteryCoinForm;
